/**
 * Sentry 유틸리티 함수
 *
 * API 에러 및 커스텀 에러를 Sentry에 전송할 때 사용합니다.
 * 태그, 컨텍스트, 레벨, 핑거프린트를 설정하여 효과적인 에러 추적이 가능합니다.
 */
import * as Sentry from '@sentry/nextjs';

// import { createHmac } from 'crypto';

// ============================================================================
// Types
// ============================================================================

interface ApiErrorOptions {
  /** HTTP 메소드 (GET, POST, PUT, DELETE 등) */
  method: string;
  /** API 엔드포인트 (예: /api/missions/submit) */
  endpoint: string;
  /** HTTP 상태 코드 */
  status: number;
  /** 요청 데이터 (민감 정보 제외) */
  request?: Record<string, unknown>;
  /** 응답 데이터 */
  response?: Record<string, unknown>;
  /** 추가 태그 */
  tags?: Record<string, string>;
}

interface CustomErrorOptions {
  /** 에러 타입 (api, validation, business 등) */
  errorType: string;
  /** 에러 레벨 */
  level?: Sentry.SeverityLevel;
  /** 추가 태그 (검색 가능) */
  tags?: Record<string, string>;
  /** 추가 컨텍스트 (검색 불가, 상세 정보) */
  context?: Record<string, Record<string, unknown>>;
  /** 커스텀 핑거프린트 (이슈 그룹핑) */
  fingerprint?: string[];
}

// ============================================================================
// Error Normalization
// ============================================================================

/**
 * 에러를 Error 객체로 정규화합니다.
 * 일반 객체 { code, details, ... } 형태를 Error 객체로 변환하여 Sentry가 더 잘 분석할 수 있도록 합니다.
 *
 * @param error - 에러 객체 (Error 인스턴스 또는 일반 객체)
 * @returns 정규화된 Error 객체
 *
 * @example
 * ```typescript
 * try {
 *   throw { code: 'VALIDATION_ERROR', details: { field: 'email' } };
 * } catch (error) {
 *   const normalizedError = normalizeError(error);
 *   Sentry.captureException(normalizedError);
 * }
 * ```
 */
export const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (error && typeof error === 'object') {
    const errorObj = error as { code?: string; details?: unknown; message?: string };
    const message = errorObj.message || errorObj.code || 'Unknown error';
    const normalizedError = new Error(message);

    // 원본 정보를 에러 객체에 보존 (Sentry 컨텍스트로 활용 가능)
    if (errorObj.code) {
      (normalizedError as Error & { code?: string }).code = errorObj.code;
    }
    if (errorObj.details) {
      (normalizedError as Error & { details?: unknown }).details = errorObj.details;
    }

    // Error의 내장 속성 목록 (덮어쓰기 방지)
    const protectedErrorProperties = new Set([
      'stack',
      'name',
      'message',
      'cause',
      'fileName',
      'lineNumber',
      'columnNumber',
      'code', // 이미 처리됨
      'details', // 이미 처리됨
    ]);

    // 원본 객체의 커스텀 속성만 에러 객체에 보존 (내장 속성 제외)
    Object.keys(errorObj).forEach(key => {
      if (!protectedErrorProperties.has(key)) {
        const value = (errorObj as Record<string, unknown>)[key];
        // 값이 정의되어 있고 null이 아닐 때만 복사
        if (value !== undefined && value !== null) {
          (normalizedError as Error & Record<string, unknown>)[key] = value;
        }
      }
    });

    return normalizedError;
  }

  return new Error(String(error) || 'Unknown error');
};

// ============================================================================
// API Error Capture
// ============================================================================

/**
 * API 에러를 Sentry에 전송합니다.
 *
 * @example
 * ```typescript
 * try {
 *   await api.post('/missions/submit', data);
 * } catch (error) {
 *   captureApiError(error, {
 *     method: 'POST',
 *     endpoint: '/missions/submit',
 *     status: 500,
 *     request: { missionId: 123 },
 *     response: { error: 'Internal Server Error' },
 *   });
 *   throw error;
 * }
 * ```
 */
export const captureApiError = (error: unknown, options: ApiErrorOptions): void => {
  const { method, endpoint, status, request, response, tags = {} } = options;

  // 🔥 Error 객체로 정규화하여 Sentry에 전달
  const normalizedError = normalizeError(error);

  // 태그 객체 구성
  const allTags: Record<string, string> = {
    'error.type': 'api',
    'api.method': method.toUpperCase(),
    'api.endpoint': endpoint,
    'api.status': String(status),
    'api.status_category': getStatusCategory(status),
    ...tags,
  };

  // 컨텍스트 객체 구성
  const contexts: Record<string, Record<string, unknown>> = {};

  if (request) {
    contexts.api_request = {
      method: method.toUpperCase(),
      endpoint,
      payload: sanitizeData(request),
    };
  }

  if (response) {
    contexts.api_response = {
      status,
      body: sanitizeData(response),
    };
  }

  // 정규화된 에러의 추가 정보를 컨텍스트로 추가
  if ('code' in normalizedError && normalizedError.code) {
    contexts.error_details = {
      code: normalizedError.code,
      ...('details' in normalizedError && normalizedError.details
        ? { details: normalizedError.details }
        : {}),
    };
  }

  // Sentry 8+ API: captureException에 scope 옵션 직접 전달
  Sentry.captureException(normalizedError, {
    tags: allTags,
    level: getErrorLevel(status),
    contexts,
    fingerprint: ['api', method.toUpperCase(), endpoint, getStatusCategory(status)],
  });
};

// ============================================================================
// Custom Error Capture
// ============================================================================

/**
 * 커스텀 에러를 Sentry에 전송합니다.
 *
 * @example
 * ```typescript
 * captureCustomError(new Error('Payment failed'), {
 *   errorType: 'payment',
 *   level: 'error',
 *   tags: { provider: 'stripe', amount: '10000' },
 *   context: {
 *     payment: { orderId: '123', userId: '456' },
 *   },
 * });
 * ```
 */
export const captureCustomError = (error: unknown, options: CustomErrorOptions): void => {
  const { errorType, level = 'error', tags = {}, context = {}, fingerprint } = options;

  // 🔥 Error 객체로 정규화하여 Sentry에 전달
  const normalizedError = normalizeError(error);

  // 태그 객체 구성
  const allTags: Record<string, string> = {
    'error.type': errorType,
    ...tags,
  };

  // 컨텍스트 객체 구성
  const contexts: Record<string, Record<string, unknown>> = {};

  // 사용자 정의 컨텍스트 추가
  Object.entries(context).forEach(([name, data]) => {
    contexts[name] = sanitizeData(data);
  });

  // 정규화된 에러의 추가 정보를 컨텍스트로 추가
  if ('code' in normalizedError && normalizedError.code) {
    contexts.error_details = {
      code: normalizedError.code,
      ...('details' in normalizedError && normalizedError.details
        ? { details: normalizedError.details }
        : {}),
    };
  }

  // Sentry 8+ API: captureException에 scope 옵션 직접 전달
  Sentry.captureException(normalizedError, {
    tags: allTags,
    level,
    contexts,
    ...(fingerprint && { fingerprint }),
  });
};

// ============================================================================
// Breadcrumb
// ============================================================================

/**
 * 사용자 행동을 Breadcrumb으로 기록합니다.
 * 에러 발생 시 디버깅에 도움이 됩니다.
 *
 * @example
 * ```typescript
 * addBreadcrumb('mission', 'User started mission', { missionId: 123 });
 * ```
 */
export const addBreadcrumb = (
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void => {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data: data ? sanitizeData(data) : undefined,
  });
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * HTTP 상태 코드에 따른 Sentry 레벨 반환
 */
const getErrorLevel = (status: number): Sentry.SeverityLevel => {
  if (status >= 500) {
    return 'error'; // 서버 에러
  }
  if (status >= 400) {
    return 'warning'; // 클라이언트 에러
  }
  return 'info';
};

/**
 * HTTP 상태 코드 카테고리 반환
 */
const getStatusCategory = (status: number): string => {
  if (status >= 500) {
    return '5xx';
  }
  if (status >= 400) {
    return '4xx';
  }
  if (status >= 300) {
    return '3xx';
  }
  if (status >= 200) {
    return '2xx';
  }
  return 'unknown';
};

// ============================================================================
// Sanitization Utilities (exported for reuse)
// ============================================================================

/** 민감한 키 목록 */
const SENSITIVE_KEYS = [
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'authorization',
  'cookie',
  'secret',
  'apiKey',
  'creditCard',
  'ssn',
  'phoneNumber',
  'email',
];

/** 민감한 URL 쿼리 파라미터 목록 */
const SENSITIVE_URL_PARAMS = [
  'token',
  'apiKey',
  'secret',
  'password',
  'accessToken',
  'refreshToken',
  'key',
  'auth',
  'code',
];

/**
 * 민감한 데이터 제거 (객체용)
 * @param data 민감한 정보가 포함될 수 있는 객체
 * @returns 민감한 필드가 [REDACTED]로 대체된 객체
 */
export const sanitizeData = <T extends Record<string, unknown>>(data: T): T => {
  const sanitized = { ...data };

  Object.keys(sanitized).forEach(key => {
    const lowerKey = key.toLowerCase();
    if (SENSITIVE_KEYS.some(sk => lowerKey.includes(sk.toLowerCase()))) {
      (sanitized as Record<string, unknown>)[key] = '[REDACTED]';
    }
  });

  return sanitized;
};

/**
 * URL에서 민감한 쿼리 파라미터 제거
 * @param url 민감한 정보가 포함될 수 있는 URL
 * @returns 민감한 파라미터가 [REDACTED]로 대체된 URL
 *
 * @example
 * sanitizeUrl('https://api.com/users?token=abc123&name=john')
 * // => 'https://api.com/users?token=[REDACTED]&name=john'
 */
export const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);

    SENSITIVE_URL_PARAMS.forEach(param => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    });

    return urlObj.toString();
  } catch {
    // URL 파싱 실패 시 경로만 반환 (보안상 안전)
    return '[INVALID_URL]';
  }
};

/**
 * 변수(mutation variables)에서 민감한 데이터 제거
 * @param variables API mutation에 전달되는 변수
 * @returns 민감한 필드가 [REDACTED]로 대체된 변수
 */
export const sanitizeVariables = (variables: unknown): unknown => {
  // null/undefined는 그대로 반환
  if (variables === null || variables === undefined) {
    return null;
  }

  // primitive 값은 그대로 반환
  if (typeof variables !== 'object') {
    return variables;
  }

  // 배열은 각 요소를 재귀적으로 sanitize
  if (Array.isArray(variables)) {
    return variables.map(item => sanitizeVariables(item));
  }

  // 객체는 sanitizeData로 처리
  return sanitizeData(variables as Record<string, unknown>);
};

// ============================================================================
// PII Hashing Utilities
// ============================================================================

/**
 * PII를 boolean 플래그로 변환합니다.
 * 값의 존재 여부만 기록하고 실제 값은 기록하지 않습니다.
 *
 * @param value 확인할 PII 값
 * @returns 값이 있으면 true, 없으면 false
 *
 * @example
 * ```typescript
 * Sentry.captureException(error, {
 *   extra: { hasTikTokHandle: hasPII(tiktokHandle) }
 * });
 * ```
 */
export const hasPII = (value: string | null | undefined): boolean => {
  return value !== null && value !== undefined && value !== '';
};
